import React from 'react';
import { createPortal } from 'react-dom';
import { Button, Typography, theme } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { IconAlertTriangle } from '@tabler/icons-react';

const { Text } = Typography;

interface ConfirmBottomSheetProps {
  open: boolean;
  title: string;
  description?: string;
  confirmText: string;
  cancelText: string;
  confirmLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Shared replacement for antd's Popconfirm on destructive actions (cancel
 * booking, delete a space, mark a no-show) -- a branded bottom sheet instead
 * of a small anchored popover, since those read as too easy to miss/tap
 * through by accident on mobile. Dismissible by tapping the backdrop or by
 * dragging the sheet itself down past a threshold (dragSnapToOrigin makes a
 * short drag spring back instead of committing to a close), on top of the
 * two explicit buttons -- the confirm side always uses colorError (this
 * app's "destructive action" semantic, same as the toast system's error
 * tone), never CORAL, since CORAL is reserved for brand/primary actions and
 * this is the opposite of that.
 */
const ConfirmBottomSheet: React.FC<ConfirmBottomSheetProps> = ({
  open,
  title,
  description,
  confirmText,
  cancelText,
  confirmLoading,
  onConfirm,
  onClose,
}) => {
  const { token } = theme.useToken();

  // Rendered via a portal straight onto <body> -- this sheet uses
  // position:fixed to anchor to the viewport, but any ancestor up the call
  // chain with a CSS transform (framer-motion page transitions add one)
  // creates its own containing block, silently rescoping "fixed" to that
  // ancestor's box instead of the real viewport and, worse, dropping this
  // whole subtree into a nested stacking context where its z-index no
  // longer competes with the rest of the page (e.g. the fixed AppHeader
  // could end up painted on top despite its lower z-index). A portal
  // sidesteps both problems the same way antd's own Modal/Drawer do.
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="confirm-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
          />
          <motion.div
            key="confirm-sheet"
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            dragSnapToOrigin
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose();
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2101,
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
              background: token.colorBgContainer,
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
              padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              touchAction: 'none',
            }}
          >
            {/* Grab handle -- purely a visual affordance for the drag gesture, not itself interactive */}
            <div style={{ width: 36, height: 4, borderRadius: 2, background: token.colorBorder, margin: '0 auto 16px' }} />
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
              <span
                style={{
                  display: 'flex',
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: token.colorWarningBg,
                  color: token.colorWarning,
                }}
              >
                <IconAlertTriangle size={20} />
              </span>
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ display: 'block', fontSize: 16 }}>
                  {title}
                </Text>
                {/* pre-line so a caller can pass more than one sentence and
                    have them land on separate lines -- the cancellation sheet
                    needs to say what is being cancelled AND what comes back,
                    and running those together makes the money part easy to
                    skim past. */}
                {description && (
                  <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'pre-line' }}>
                    {description}
                  </Text>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button block style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }} onClick={onClose}>
                {cancelText}
              </Button>
              <Button
                block
                loading={confirmLoading}
                onClick={onConfirm}
                style={{ borderRadius: 999, background: token.colorError, borderColor: token.colorError, color: '#fff' }}
              >
                {confirmText}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ConfirmBottomSheet;
